import React, { ComponentProps, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  History,
  HowToSaveType,
  Target,
  TargetHistoryTransitionData,
  howToSaveInvestmentProducts,
} from 'components/features/target-achievement-support/utils/type';
import { getTargetHistory } from 'config/api';
import {
  ExtraTimeSeriesData,
  TimeSeriesDatum,
} from 'components/features/home/assetStuffing/AssetStuffing';
import { AllAICheer } from './rangeTab/AllAICheer';
import { RemainAmount } from './rangeTab/RemainAmount';
import { TransitionChart } from './TransitionChart';
import { ChartLoading } from './Month';

const AllTimeRemainAmount = ({
  currentAmount,
  targetAmount,
  className,
}: Omit<
  ComponentProps<typeof RemainAmount>,
  'period' | 'limit'
>): React.ReactElement => {
  return (
    <>
      <RemainAmount
        {...{
          currentAmount,
          limit: { period: 'alltime' },
          targetAmount,
          className,
        }}
      />
    </>
  );
};

const AllTimeAICheer = ({
  achieved,
  title,
  currentAmount,
  targetAmount,
  benchmarkAmount,
  targetStartDate,
  targetEndDate,
  requiredYield,
  goHowToIncreaseAccumulationAmount,
  className,
}: {
  achieved: boolean;
  title: string;
  currentAmount: number;
  targetAmount: number;
  benchmarkAmount: number;
  targetStartDate: Date;
  targetEndDate: Date;
  requiredYield: number | null;
  goHowToIncreaseAccumulationAmount: () => void;
  className?: string;
}): React.ReactElement => {
  return (
    <>
      <div
        className={`rounded-lg flex flex-row items-start gap-4 border border-bluegray-300 bg-bluegray-100 p-4 ${className ??
          ''}`}>
        <AllAICheer
          {...{
            achieved,
            title,
            currentAmount,
            targetAmount,
            benchmarkAmount,
            targetStartDate,
            targetEndDate,
            requiredYield,
            goHowToIncreaseAccumulationAmount,
          }}
        />
      </div>
    </>
  );
};

type Props = {
  goHowToIncreaseAccumulationAmount: () => void;
  target: Target;
  targetHowToSaveType: HowToSaveType;
};

export const AllTime = (props: Props): React.ReactElement => {
  const { data: history } = useQuery(
    'allTimeTargethistory',
    () => getTargetHistoryAPI(props.target.id),
    {
      refetchOnWindowFocus: false,
    }
  );

  const chartIncorrectData = useMemo(() => {
    if (history === null || history === undefined) return null;

    const transitions = history.transitions as TargetHistoryTransitionData[];
    const incorrectSign: ExtraTimeSeriesData = {
      type: 'IncorrectSign',
      value: transitions.map(it => {
        return {
          value: it.incorrect,
          date: new Date(it.date),
        } as TimeSeriesDatum;
      }),
    };
    return [incorrectSign];
  }, [history]);

  if (history === null || history === undefined || chartIncorrectData === null)
    return <ChartLoading />;

  const from = new Date(history.from);
  const to = new Date(history.to);

  const rangeStr = ((): string => {
    const toString = (date: Date): string =>
      `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    return `${toString(from)}〜${toString(to)}`;
  })();

  const currentAmount = history.achievementAmount;
  const targetAmountForDisplay = history.targetAmount;

  const achieved = props.target.achieved;
  const benchmarkAmount = history.transitions[0]?.benchmarkAmount ?? null;
  const targetStartDate = new Date(props.target.startDate);
  const targetEndDate = new Date(props.target.endDate);
  const requiredYield =
    props.target.reserveAmount !== null &&
    props.target.excludeAmount !== null &&
    props.target.accountList !== null
      ? null
      : props.targetHowToSaveType === howToSaveInvestmentProducts
      ? null
      : (annualYield => Math.ceil(annualYield * 1000) / 10)(props.target.yield);

  const chartData =
    history.transitions[history.transitions.length - 1]?.benchmarkAmount ??
    history.targetAmount;

  const transitions = history.transitions as TargetHistoryTransitionData[];

  return (
    <>
      <div className="">
        <div className="bg-white py-4">
          <div className="flex flex-col items-center justify-center">
            <div className="flex flex-row items-center justify-center mt-1 ml-3 font-bold text-lg">
              全期間
            </div>
            <div className="text-bluegray-700 text-sm">{rangeStr}</div>
          </div>
          <AllTimeRemainAmount
            {...{ currentAmount, targetAmount: targetAmountForDisplay }}
            className="mt-2"
          />
          <TransitionChart
            {...{
              dateFormatter: date => `${date.getFullYear()}`,
              targetDate: targetEndDate,
              data: {
                dataRange: {
                  from: targetStartDate,
                  to: targetEndDate,
                },
                achievements: transitions.map(item => ({
                  achievement: item.assetAmount,
                  date: new Date(item.date),
                })),
                benchmarks: transitions.map(item => ({
                  benchmark: item.benchmarkAmount,
                  date: new Date(item.date),
                })),
                targetAmount: chartData,
                extraData: chartIncorrectData,
              },
            }}
          />
          <AllTimeAICheer
            achieved={achieved}
            benchmarkAmount={benchmarkAmount}
            targetStartDate={targetStartDate}
            targetEndDate={targetEndDate}
            title={props.target.title}
            currentAmount={currentAmount}
            targetAmount={targetAmountForDisplay}
            requiredYield={requiredYield}
            goHowToIncreaseAccumulationAmount={
              props.goHowToIncreaseAccumulationAmount
            }
            className="mt-4 mx-4"
          />
        </div>
      </div>
    </>
  );
};

const getTargetHistoryAPI = async (id: string) => {
  const ret = await getTargetHistory(id, '');

  return {
    id: ret.ID,
    achievementAmount: ret.AchievementAmount,
    targetAmount: ret.TargetAmount,
    from: ret.From,
    to: ret.To,
    transitions: ret.Transitions.map(item => {
      return {
        assetAmount: item.AssetAmount,
        benchmarkAmount: item.BenchmarkAmount,
        date: item.Date,
        incorrect: item.Incorrect,
      };
    }),
  } as History;
};
